"use client";

import { useEffect, useId, useRef, useState } from "react";
import { groupLayouts } from "../layout-grouping";
import { LayoutGroupCard } from "../layout-groups";
import { LayoutPreview, LayoutPreviewProvider } from "../layout-preview";
import { DrillIcon } from "./icon";
import { DrillError } from "./shared";
import type { Layout } from "./types";

export function LayoutDialog({
  layouts,
  error,
  excludedIds,
  onRetry,
  onClose,
  onSelect,
  returnFocus,
}: {
  layouts?: Layout[];
  error: string;
  excludedIds: string[];
  onRetry: () => void;
  onClose: () => void;
  onSelect: (layout: Layout) => void;
  returnFocus: HTMLElement | null;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const headingId = useId();
  const radioName = useId();
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState("");
  useEffect(() => {
    const element = dialog.current;
    const overflow = document.body.style.overflow;
    element?.showModal();
    document.body.style.overflow = "hidden";
    if (!window.matchMedia("(max-width: 560px)").matches)
      search.current?.focus();
    else element?.querySelector<HTMLElement>("h2")?.focus();
    return () => {
      element?.close();
      document.body.style.overflow = overflow;
      returnFocus?.focus({ preventScroll: true });
    };
  }, [returnFocus]);
  const available = layouts?.filter(
    (layout) => !excludedIds.includes(layout.id),
  );
  const groups = layouts
    ? groupLayouts(layouts, query).filter((group) =>
        [group.main, ...group.subs].some(
          (layout) => !excludedIds.includes(layout.id),
        ),
      )
    : [];
  const results = groups
    .flatMap((group) => [group.main, ...group.subs])
    .filter((layout) => !excludedIds.includes(layout.id));
  const selected = results?.find((layout) => layout.id === selectedId);
  return (
    <dialog
      ref={dialog}
      className="drill-layout-dialog"
      aria-labelledby={headingId}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      <div className="drill-layout-dialog-heading">
        <div>
          <h2 id={headingId} tabIndex={-1}>
            既存の配置図を使用
          </h2>
        </div>
        <button
          type="button"
          className="drill-layout-arrow"
          aria-label="配置図の選択を閉じる"
          onClick={onClose}
        >
          <DrillIcon name="miss" />
        </button>
      </div>
      <label className="drill-layout-search">
        <DrillIcon name="search" size={18} />
        <span className="drill-sr-only">配置図名で検索</span>
        <input
          ref={search}
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="配置図名で検索"
        />
      </label>
      <div className="drill-layout-dialog-results">
        {error ? (
          <>
            <DrillError message={error} />
            <button
              type="button"
              className="secondary-button"
              onClick={onRetry}
            >
              再読み込み
            </button>
          </>
        ) : !layouts ? (
          <p role="status">配置図を読み込み中…</p>
        ) : layouts.length === 0 ? (
          <div className="drill-layout-dialog-empty">
            <h3>保存した配置図がありません</h3>
            <p>この画面を閉じて「配置図を作成し使用」から作成できます。</p>
          </div>
        ) : !results?.length ? (
          <div className="drill-layout-dialog-empty">
            <h3>
              {available?.length
                ? "一致する配置図がありません"
                : "すべての配置図を登録済みです"}
            </h3>
            {query && (
              <button
                type="button"
                className="text-link"
                onClick={() => setQuery("")}
              >
                検索をクリア
              </button>
            )}
          </div>
        ) : (
          <>
            <p className="drill-layout-result-count" role="status">
              {results.length}件
              {excludedIds.length > 0
                ? " · 登録済みの配置図は選択できません"
                : ""}
            </p>
            <LayoutPreviewProvider>
              <div className="drill-layout-group-choices">
                {groups.map((group) => (
                  <LayoutGroupCard
                    key={`${group.main.id}:${query.trim() ? "search" : "all"}`}
                    group={group}
                    initiallyExpanded={!!query.trim()}
                    renderLayout={(layout, _role, roleLabelId) => (
                      <div
                        className={`drill-layout-choice${selectedId === layout.id ? " is-selected" : ""}`}
                      >
                        <LayoutPreview {...layout} href={null} />
                        <label>
                          <input
                            type="radio"
                            name={radioName}
                            aria-labelledby={`${roleLabelId} ${headingId}-${layout.id}`}
                            value={layout.id}
                            checked={selectedId === layout.id}
                            disabled={excludedIds.includes(layout.id)}
                            onChange={() => setSelectedId(layout.id)}
                          />
                          <span id={`${headingId}-${layout.id}`}>
                            {layout.title}
                          </span>
                          {excludedIds.includes(layout.id) ? (
                            <small>登録済み</small>
                          ) : (
                            selectedId === layout.id && <small>選択中</small>
                          )}
                        </label>
                      </div>
                    )}
                  />
                ))}
              </div>
            </LayoutPreviewProvider>
          </>
        )}
      </div>
      <div className="drill-layout-dialog-footer">
        <span>{selected ? selected.title : "配置図を1つ選択してください"}</span>
        <div>
          <button type="button" className="secondary-button" onClick={onClose}>
            キャンセル
          </button>
          <button
            type="button"
            className="primary-button"
            disabled={!selected}
            onClick={() => {
              if (selected) onSelect(selected);
            }}
          >
            この配置図を使用 <DrillIcon name="check" size={17} />
          </button>
        </div>
      </div>
    </dialog>
  );
}
